import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ApolloProvider } from '@apollo/client';
import '@cloudscape-design/global-styles/index.css';
import 'src/styles/global.scss';
import App from './App';
import { getApolloClient } from './api/client';
import { DarkModeProvider } from './common/ui/DarkModeContext';
import ArbProvider from './i18n/ArbProvider';
import { initRum } from './common/rum/rum';

// Injected by webpack DefinePlugin (see configuration/webpack.base.config.js).
// When true the app runs entirely against mocked GraphQL data with no backend
// connectivity (see the `server-mock` / `server-mock-noauth` scripts).
declare const MOCK_GRAPHQL: boolean;

// Initialize CloudWatch RUM eagerly at startup, BEFORE the app mounts. RUM authorizes via Midway
// (the credential provider fetches a Midway token and exchanges it via STS on first emit), so init
// itself does no network I/O and must not block the app — doing it here guarantees the client
// exists before the first route-change page-view effect fires, so the first page view (typically
// Landing) is recorded instead of being dropped by an init-order race.
// Safe no-op when RUM is unconfigured for this build/stage.
//
// Skip under Cypress. Cypress runs on Hydra authenticate the browser via SAAM
// (`@amzn/cypress-midway-plugin` — a SigV4-obtained Midway cookie for the app
// origin), NOT with the personal-user Midway id token the RUM role's trust
// policy accepts. `STS AssumeRoleWithWebIdentity` therefore rejects, and the
// AwsRum SDK surfaces the failure as an unhandled promise rejection. The test
// package's `cypress/support/index.ts` only swallows `ResizeObserver loop…`,
// so any RUM rejection reaches Cypress' default `uncaught:exception` handler
// and fails the current test — typically as a spec's second-or-later
// `cy.visit()` timing out on `h1`. Cypress runs are also not real users, so
// their page views would be noise in the RUM dashboard even if they did
// succeed. `window.Cypress` is set on the AUT window whenever a Cypress
// runner is driving the page — checking it here disables RUM in the one
// context where it breaks the tests.
const isCypressRun =
  typeof window !== 'undefined' &&
  (window as Window & { Cypress?: unknown }).Cypress !== undefined;
if (!isCypressRun) {
  initRum();
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');
const root = createRoot(container);

// Rendering is async because building the mock link may await asynchronously
// generated mock data (matching the FelineUIWebsite pattern).
const renderApp = async () => {
  const app = (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );

  let withGraphQL;
  if (MOCK_GRAPHQL) {
    const { MockedProvider } = await import('@apollo/client/testing');
    const { GQLWildcardLink } = await import('./api/mocks/mocks');
    withGraphQL = (
      <MockedProvider link={await GQLWildcardLink()} addTypename={false}>
        {app}
      </MockedProvider>
    );
  } else {
    withGraphQL = (
      <ApolloProvider client={getApolloClient()}>{app}</ApolloProvider>
    );
  }

  root.render(
    <React.StrictMode>
      <DarkModeProvider>
        <ArbProvider>{withGraphQL}</ArbProvider>
      </DarkModeProvider>
    </React.StrictMode>,
  );
};

void renderApp();
